/**
 * Session-level helpers for the Conversation Analysis view.
 *
 * The view is session-centric: one row per raw session. The instant-feedback
 * mechanism spawns its own session that asks the student about their *previous*
 * experience, so a feedback-only session stands on its own as a feedback row
 * rather than being folded into the conversation it follows up on.
 *
 * Recency always uses `created` (when the conversation happened); `updated`
 * shifts when a session is re-analyzed.
 */

export function sessionDate(s) {
    return s.created || s.updated || ""
}

/**
 * Parses a DB timestamp like "2026-05-13 14:41:36.809067+00" (space-separated,
 * short "+00" offset) into epoch ms. Returns NaN for missing/unparseable input.
 */
export function parseTs(s) {
    if (!s) return NaN
    return new Date(s.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00")).getTime()
}

export function sessionStart(s) {
    const ts = (s.messages || []).map(m => parseTs(m.timestamp)).filter(t => !isNaN(t))
    return ts.length ? Math.min(...ts) : parseTs(sessionDate(s))
}

export function buildSupportNeedAttribution(sessions, supportNeeds, toleranceMs = 5 * 60_000) {
    const byKey = new Map()
    for (const s of sessions) {
        const k = `${s.contact}|${s.survey}`
        if (!byKey.has(k)) byKey.set(k, [])
        byKey.get(k).push({s, start: sessionStart(s)})
    }
    for (const arr of byKey.values()) arr.sort((a, b) => a.start - b.start)

    const map = new Map()
    for (const need of supportNeeds || []) {
        const arr = byKey.get(`${need.contact}|${need.survey}`)
        if (!arr?.length) continue
        const needT = parseTs(need.created)
        let chosen = null
        for (const {s, start} of arr) {
            if (start <= needT + toleranceMs) chosen = s
            else break
        }
        if (!chosen) continue
        if (!map.has(chosen.id)) map.set(chosen.id, [])
        map.get(chosen.id).push(need)
    }
    return map
}

export function isBlockedSession(s) {
    const a = s.llmanalysis
    return !!(a && (a.error === "BLOCKED" || a.blocked === true))
}

export function sessionHasFeedback(s) {
    const a = s.llmanalysis
    return !!(a && (a.feedback_response != null || a.feedback_asked))
}

/**
 * The analysis classifier for a session: "support" | "feedback_only" |
 * "feedback_then_support" (see analysis-agent.json). Every analyzed session
 * carries one; null only when unanalyzed.
 */
export function sessionKind(s) {
    return s.llmanalysis?.session_kind ?? null
}

export function isFeedbackOnly(s) {
    return sessionKind(s) === "feedback_only"
}

/**
 * Outcome of a single session. `status` reuses the row's icon classes
 * (success | failed | uncertain | blocked | unanalyzed | feedback). Derived from
 * the session's OWN analysis only — feedback sessions are their own rows now, so
 * no folding from neighbours.
 *
 *   { status, feedback }   feedback = "positive" | "negative" | null (feedback rows)
 */
export function getSessionOutcome(session) {
    if (!session) return {status: "unanalyzed", feedback: null}

    const analysis = session.llmanalysis
    if (isBlockedSession(session)) return {status: "blocked", feedback: null}
    if (!analysis) return {status: "unanalyzed", feedback: null}

    if (isFeedbackOnly(session)) {
        return {status: "feedback", feedback: analysis.feedback_response ?? null}
    }

    const stopPoint = analysis.conversation_stop_point ?? null
    let status
    if (analysis.conversation_success === false || stopPoint === "bot_error") {
        status = "failed"
    } else if (analysis.conversation_success === true) {
        status = "success"
    } else {
        status = "uncertain"
    }
    return {status, feedback: analysis.feedback_response ?? null}
}

const STOP_POINT_DEFAULTS = {
    resolved: "Resolved",
    referred: "Referred",
    user_disengaged: "Student disengaged",
    user_declined: "Student declined",
    no_engagement: "No engagement",
    bot_error: "Bot error",
    other: "Other",
}

/** Localized human-readable label for a conversation_stop_point code. */
export function getStopPointLabel(code, intl) {
    if (!code) return null
    const fallback = STOP_POINT_DEFAULTS[code] || code
    return intl.formatMessage({id: `analysis.stopPoint.${code}`, defaultMessage: fallback})
}
